// RFC 5545 .ics builder for guidedsport game events.

import { platformInfo } from "@/lib/streamers";

const DEFAULT_DURATION_MIN = 180;
const SPORT_DURATION_MIN = {
  "NFL": 210,
  "College Football": 210,
  "NBA": 150,
  "WNBA": 150,
  "Men's College Basketball": 130,
  "Women's College Basketball": 130,
  "MLB": 180,
  "NHL": 150,
  "MLS": 120,
  "FIFA World Cup 2026": 130,
  "Tennis": 180,
  "Golf": 240,
  "Men's College Volleyball": 120,
  "Women's College Volleyball": 120,
};

function pad(n) { return String(n).padStart(2, "0"); }

function toUtcStamp(date) {
  return (
    date.getUTCFullYear().toString() +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate()) +
    "T" +
    pad(date.getUTCHours()) +
    pad(date.getUTCMinutes()) +
    pad(date.getUTCSeconds()) +
    "Z"
  );
}

function escapeText(s) {
  if (s == null) return "";
  return String(s)
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

// Fold lines longer than 75 octets per RFC 5545 §3.1
function foldLine(line) {
  if (line.length <= 75) return line;
  const out = [];
  let i = 0;
  while (i < line.length) {
    const chunk = line.slice(i, i + (i === 0 ? 75 : 74));
    out.push((i === 0 ? "" : " ") + chunk);
    i += i === 0 ? 75 : 74;
  }
  return out.join("\r\n");
}

function vevent(game) {
  if (!game?.start_iso) return null;
  const start = new Date(game.start_iso);
  if (isNaN(start.getTime())) return null;

  const minutes = SPORT_DURATION_MIN[game.sport] ?? DEFAULT_DURATION_MIN;
  const end = new Date(start.getTime() + minutes * 60 * 1000);
  const stamp = toUtcStamp(new Date());

  const platforms = (game.platforms && game.platforms.length > 0)
    ? game.platforms
    : (game.platform ? [game.platform] : []);
  const platformLabels = platforms.map((p) => platformInfo(p).label);

  const uid = `${game.id || `${start.getTime()}-${game.away_team}-${game.home_team}`}@guidedsport`;
  const summary = `${game.away_team} @ ${game.home_team} (${game.sport})`;
  const descLines = [];
  if (platformLabels.length) descLines.push(`Watch: ${platformLabels.join(", ")}`);
  if (game.source_url) descLines.push(`Source: ${game.source_url}`);
  descLines.push("via guidedsport");
  const description = descLines.join("\\n");

  const lines = [
    "BEGIN:VEVENT",
    `UID:${escapeText(uid)}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${toUtcStamp(start)}`,
    `DTEND:${toUtcStamp(end)}`,
    `SUMMARY:${escapeText(summary)}`,
    `DESCRIPTION:${description}`,
    game.source_url ? `URL:${escapeText(game.source_url)}` : null,
    "END:VEVENT",
  ].filter(Boolean);

  return lines.map(foldLine).join("\r\n");
}

export function gamesToIcs(games) {
  const events = (games || []).map(vevent).filter(Boolean);
  if (events.length === 0) return null;
  const cal = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//guidedsport//Sports Streaming Tracker//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    ...events,
    "END:VCALENDAR",
  ].join("\r\n");
  return cal;
}

export function gameToIcs(game) {
  return gamesToIcs([game]);
}

function safeFilename(s) {
  return String(s).replace(/[^a-z0-9-_]+/gi, "-").replace(/-+/g, "-").slice(0, 80);
}

export function downloadIcs(filename, icsText) {
  if (!icsText) return false;
  const blob = new Blob([icsText], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = safeFilename(filename) + ".ics";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

export function isExportable(game) {
  return Boolean(game?.start_iso) && !isNaN(new Date(game.start_iso).getTime());
}
